// Maps sectionSchema.ts's raw English label strings — the model layer stays
// plain English — to a TextKey, for PropertyPanel.tsx to translate at render
// time.

import type { TextKey } from "./en.ts";

const SECTION_LABEL_KEYS: Record<string, TextKey> = {
  JUNCTIONS: "section.JUNCTIONS",
  OUTFALLS: "section.OUTFALLS",
  STORAGE: "section.STORAGE",
  DIVIDERS: "section.DIVIDERS",
  CONDUITS: "section.CONDUITS",
  PUMPS: "section.PUMPS",
  ORIFICES: "section.ORIFICES",
  WEIRS: "section.WEIRS",
  OUTLETS: "section.OUTLETS",
  XSECTIONS: "section.XSECTIONS",
  SUBCATCHMENTS: "section.SUBCATCHMENTS",
  SUBAREAS: "section.SUBAREAS",
  // INFILTRATION deliberately excluded — see infiltrationLabelKey below.
  RAINGAGES: "section.RAINGAGES",
};

/** `group.section` (e.g. "JUNCTIONS") -> its translated group header key — `null` for INFILTRATION, whose label PropertyPanel.tsx resolves separately via `infiltrationLabelKey`. */
export function sectionLabelKey(section: string): TextKey | null {
  return SECTION_LABEL_KEYS[section] ?? null;
}

// INFILTRATION's group label switches on the project's own method rather
// than being fixed by section name, so it can't go in SECTION_LABEL_KEYS
// above.
const INFILTRATION_LABEL_KEYS: Record<string, TextKey> = {
  "Infiltration (Horton)": "section.INFILTRATION_HORTON",
  "Infiltration (Modified Horton)": "section.INFILTRATION_MODIFIED_HORTON",
  "Infiltration (Green Ampt)": "section.INFILTRATION_GREEN_AMPT",
  "Infiltration (Modified Green Ampt)": "section.INFILTRATION_MODIFIED_GREEN_AMPT",
  "Infiltration (Curve Number)": "section.INFILTRATION_CURVE_NUMBER",
};

/** `group.label`'s own raw value for an INFILTRATION group (already the fully-formatted "Infiltration (Method)" string sectionSchema.ts produces) -> its translated key. */
export function infiltrationLabelKey(rawLabel: string): TextKey | null {
  return INFILTRATION_LABEL_KEYS[rawLabel] ?? null;
}

const KIND_LABEL_KEYS: Record<string, TextKey> = {
  junction: "kind.junction",
  outfall: "kind.outfall",
  storage: "kind.storage",
  divider: "kind.divider",
  conduit: "kind.conduit",
  pump: "kind.pump",
  orifice: "kind.orifice",
  weir: "kind.weir",
  outlet: "kind.outlet",
  subcatchment: "kind.subcatchment",
  raingage: "kind.raingage",
};

export function kindLabelKey(kind: string): TextKey | null {
  return KIND_LABEL_KEYS[kind] ?? null;
}

// One entry per distinct column label string in sectionSchema.ts's
// SECTION_SCHEMAS. A column reused across sections (e.g. "From Node" in
// CONDUITS/PUMPS/ORIFICES/WEIRS/OUTLETS) shares one key here too. The
// exception is SUBCATCHMENTS' own "Outlet" column (which node it drains to)
// — spelled the same as the OUTLETS section/"outlet" link kind but a
// different concept, so it gets its own key (`field.subcatchmentOutlet`)
// rather than colliding with `kind.outlet`.
const FIELD_LABEL_KEYS: Record<string, TextKey> = {
  "Invert Elevation": "field.invertElevation",
  "Max Depth": "field.maxDepth",
  "Init Depth": "field.initDepth",
  "Surcharge Depth": "field.surchargeDepth",
  "Ponded Area": "field.pondedArea",
  "Outfall Type": "field.outfallType",
  "Stage/Table/Timeseries": "field.stageTableTimeseries",
  "Tide Gate": "field.tideGate",
  Shape: "field.shape",
  "Coeff./Curve A": "field.coeffCurveA",
  "Coeff. B": "field.coeffB",
  "Coeff. C": "field.coeffC",
  "Evap Fraction": "field.evapFraction",
  "Suction Head": "field.suctionHead",
  Conductivity: "field.conductivity",
  "Initial Deficit": "field.initialDeficit",
  Elevation: "field.elevation",
  "Diverted Link": "field.divertedLink",
  "Divider Type": "field.dividerType",
  "Param 1": "field.param1",
  "Param 2": "field.param2",
  "Param 3": "field.param3",
  "Param 4": "field.param4",
  "Param 5": "field.param5",
  "From Node": "field.fromNode",
  "To Node": "field.toNode",
  Length: "field.length",
  Roughness: "field.roughness",
  "Inlet Offset": "field.inletOffset",
  "Outlet Offset": "field.outletOffset",
  "Init Flow": "field.initFlow",
  "Max Flow": "field.maxFlow",
  "Pump Curve": "field.pumpCurve",
  Status: "field.status",
  "Startup Depth": "field.startupDepth",
  "Shutoff Depth": "field.shutoffDepth",
  "Orifice Type": "field.orificeType",
  "Crest Height": "field.crestHeight",
  "Discharge Coeff.": "field.dischargeCoeff",
  "Flap Gate": "field.flapGate",
  "Open/Close Time": "field.openCloseTime",
  "Weir Type": "field.weirType",
  "End Contractions": "field.endContractions",
  "End Coeff.": "field.endCoeff",
  "Rating Type": "field.ratingType",
  Geom1: "field.geom1",
  Geom2: "field.geom2",
  Geom3: "field.geom3",
  Geom4: "field.geom4",
  Barrels: "field.barrels",
  "Culvert Code": "field.culvertCode",
  Raingage: "field.raingage",
  Area: "field.area",
  "%Imperv": "field.percentImperv",
  Width: "field.width",
  "%Slope": "field.percentSlope",
  "Curb Length": "field.curbLength",
  "Snow Pack": "field.snowPack",
  "N-Imperv": "field.nImperv",
  "N-Perv": "field.nPerv",
  "S-Imperv": "field.sImperv",
  "S-Perv": "field.sPerv",
  "%Zero-Imperv": "field.percentZeroImperv",
  "Route To": "field.routeTo",
  "%Routed": "field.percentRouted",
  "Max Rate": "field.maxRate",
  "Min Rate": "field.minRate",
  "Decay Constant": "field.decayConstant",
  "Dry Time": "field.dryTime",
  "Max Infiltration": "field.maxInfiltration",
  "Curve Number": "field.curveNumber",
  "Drying Time": "field.dryingTime",
  Format: "field.format",
  Interval: "field.interval",
  SCF: "field.scf",
  "Source Type": "field.sourceType",
  "Source Name": "field.sourceName",
};

/** `null` for a raw label with no mapping — PropertyPanel.tsx falls back to the raw English label itself rather than crashing if this ever drifts out of sync (guarded by test/fieldLabels.test.ts). */
export function fieldLabelKey(section: string, rawLabel: string): TextKey | null {
  if (section === "SUBCATCHMENTS" && rawLabel === "Outlet") return "field.subcatchmentOutlet";
  return FIELD_LABEL_KEYS[rawLabel] ?? null;
}

// One entry per distinct raw value `columnOptions()` (sectionSchema.ts) can
// return, across every closed-set column — a value reused across columns
// (e.g. "TABULAR" in both STORAGE's own Shape and DIVIDERS' own Divider
// Type) shares one key here too. Flow-unit strings like "CFS"/"CMS" are
// deliberately NOT here — those stay verbatim, real unit abbreviations, not
// SWMM keyword vocabulary. `SUBAREAS`' own "OUTLET" value (route this
// sub-area's runoff to the subcatchment's own outlet) is deliberately its
// own key rather than reusing `kind.outlet` (the Outlet flow-control
// structure) — same disambiguation as SUBCATCHMENTS' "Outlet" column above.
const OPTION_LABEL_KEYS: Record<string, TextKey> = {
  // SUBAREAS "Route To" (RunoffRoutingWords)
  OUTLET: "option.subareaOutlet",
  IMPERV: "option.imperv",
  PERV: "option.perv",
  // PUMPS Status (OffOnWords)
  OFF: "option.off",
  ON: "option.on",
  // XSECTIONS Shape (XsectTypeWords)
  DUMMY: "option.dummy",
  CIRCULAR: "option.circular",
  FILLED_CIRCULAR: "option.filledCircular",
  RECT_CLOSED: "option.rectClosed",
  RECT_OPEN: "option.rectOpen",
  TRAPEZOIDAL: "option.trapezoidal",
  TRIANGULAR: "option.triangular",
  PARABOLIC: "option.parabolic",
  POWER: "option.power",
  RECT_TRIANGULAR: "option.rectTriangular",
  RECT_ROUND: "option.rectRound",
  MODBASKETHANDLE: "option.modBasketHandle",
  HORIZ_ELLIPSE: "option.horizEllipse",
  VERT_ELLIPSE: "option.vertEllipse",
  ARCH: "option.arch",
  EGG: "option.egg",
  HORSESHOE: "option.horseshoe",
  GOTHIC: "option.gothic",
  CATENARY: "option.catenary",
  SEMIELLIPTICAL: "option.semielliptical",
  BASKETHANDLE: "option.basketHandle",
  SEMICIRCULAR: "option.semicircular",
  IRREGULAR: "option.irregular",
  CUSTOM: "option.custom",
  FORCE_MAIN: "option.forceMain",
  STREET: "option.street",
  // STORAGE Shape (RelationWords) — TABULAR/FUNCTIONAL/PARABOLIC shared with other columns below.
  CYLINDRICAL: "option.cylindrical",
  CONICAL: "option.conical",
  PYRAMIDAL: "option.pyramidal",
  // DIVIDERS Divider Type (DividerTypeWords) — TABULAR shared with STORAGE above.
  CUTOFF: "option.cutoff",
  WEIR: "option.weir",
  OVERFLOW: "option.overflow",
  TABULAR: "option.tabular",
  FUNCTIONAL: "option.functional",
  // ORIFICES/WEIRS/OUTLETS Flap Gate (YesNoWords)
  NO: "option.no",
  YES: "option.yes",
  // ORIFICES Orifice Type (OrificeTypeWords)
  SIDE: "option.side",
  BOTTOM: "option.bottom",
  // WEIRS Weir Type (WeirTypeWords) — TRAPEZOIDAL shared with XSECTIONS above.
  TRANSVERSE: "option.transverse",
  SIDEFLOW: "option.sideflow",
  "V-NOTCH": "option.vNotch",
  ROADWAY: "option.roadway",
  // OUTFALLS Outfall Type (OutfallTypeWords) — TIMESERIES shared with RAINGAGES below.
  FREE: "option.free",
  NORMAL: "option.normal",
  FIXED: "option.fixed",
  TIDAL: "option.tidal",
  TIMESERIES: "option.timeseries",
  // OUTLETS Rating Type — a single combined token (sectionSchema.ts's own header explains why).
  "TABULAR/DEPTH": "option.tabularDepth",
  "TABULAR/HEAD": "option.tabularHead",
  "FUNCTIONAL/DEPTH": "option.functionalDepth",
  "FUNCTIONAL/HEAD": "option.functionalHead",
  // RAINGAGES Format (RainTypeWords)
  INTENSITY: "option.intensity",
  VOLUME: "option.volume",
  CUMULATIVE: "option.cumulative",
  // RAINGAGES Source Type (GageDataWords) — TIMESERIES shared with OUTFALLS above.
  FILE: "option.file",
  // Shared vocabulary from other project-data editors (InletsEditor.tsx,
  // CurvesEditor.tsx, PatternsEditor.tsx, LandUsesEditor.tsx,
  // PollutantsEditor.tsx). NONE/AREA/CURB/RATING/CUSTOM/TIDAL above are
  // shared with whichever other column already uses that exact word.
  NONE: "option.none",
  // InletsEditor.tsx [INLETS] Type (InletTypeWords, inletCapture.ts)
  GRATE: "option.grate",
  CURB: "option.curb",
  SLOTTED: "option.slotted",
  DROP_GRATE: "option.dropGrate",
  DROP_CURB: "option.dropCurb",
  // CurvesEditor.tsx [CURVES] Type (CurveTypeWords) — PUMP1-5 stay as
  // literal codes (not real English words to translate); TIDAL shared with
  // OUTFALLS.
  STORAGE: "option.storage",
  RATING: "option.rating",
  CONTROL: "option.control",
  DIVERSION: "option.diversion",
  SHAPE: "option.shape",
  // PatternsEditor.tsx [PATTERNS] Type (TimePatternWords)
  MONTHLY: "option.monthly",
  DAILY: "option.daily",
  HOURLY: "option.hourly",
  WEEKEND: "option.weekend",
  // LandUsesEditor.tsx's own buildup/washoff function columns
  // (BuildupTypeWords/WashoffTypeWords) — POW/EXP/SAT/EXT/EMC each expanded
  // to a real word/phrase rather than left as the raw 3-4 letter code. POW
  // reuses XSECTIONS Shape's own existing `option.power` key — same
  // "power function" concept (y = C1*x^C2) either way.
  POW: "option.power",
  EXP: "option.exponential",
  SAT: "option.saturation",
  EXT: "option.external",
  EMC: "option.eventMeanConcentration",
  // LandUsesEditor.tsx's own washoff/buildup normalizer column
  AREA: "option.area",
};

/** A raw `columnOptions()` value (sectionSchema.ts) -> its translated key — `null` for a value with no mapping (guarded by test/fieldLabels.test.ts; PropertyPanel.tsx falls back to the raw value itself). */
export function optionLabelKey(rawValue: string): TextKey | null {
  return OPTION_LABEL_KEYS[rawValue] ?? null;
}
